import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DEFAULT_PLANS,
  LIFECYCLE,
  TENANT_STATUS_DEFS,
  TENANT_STATUS_MAP,
  type TenantStatus,
} from '@goyone/shared';
import { date, money } from '@ui/lib/format';
import {
  Badge,
  EmptyState,
  ErrorState,
  Modal,
  PageHead,
  Pager,
  TableSkeleton,
} from '@ui/components/ui';
import { Icon } from '@ui/components/Icon';
import { api, qs } from '../lib/api';

interface TenantRow {
  id: string;
  slug: string;
  name: string;
  email: string | null;
  status: TenantStatus;
  trialEndsAt: string | null;
  createdAt: string;
  userCount: number;
  subscription: {
    price: string;
    currentPeriodEnd: string;
    plan: { id: string; code: string; name: string };
  } | null;
}

interface TenantPage {
  items: TenantRow[];
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
}

export function Tenants() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<TenantStatus | ''>('');
  const [page, setPage] = useState(1);
  const [creating, setCreating] = useState(false);

  const query = { q: search || undefined, status: status || undefined, page };

  const list = useQuery({
    queryKey: ['tenants', query],
    queryFn: () => api<TenantPage>(`/tenants${qs(query)}`),
  });

  return (
    <div className="page">
      <PageHead
        title="المستأجرون"
        subtitle={list.data ? `${list.data.total} شركة على المنصة` : undefined}
        actions={
          <button className="btn btn-primary" onClick={() => setCreating(true)}>
            <Icon name="plus" size={15} />
            تهيئة شركة
          </button>
        }
      />

      <div className="toolbar">
        <input
          className="input"
          value={search}
          placeholder="بحث بالنطاق أو الاسم أو البريد"
          aria-label="بحث"
          style={{ maxWidth: 340 }}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />

        <div className="row" style={{ gap: 2 }}>
          <button
            className={`btn btn-sm${!status ? ' btn-primary' : ''}`}
            onClick={() => {
              setStatus('');
              setPage(1);
            }}
          >
            الكل
          </button>
          {TENANT_STATUS_DEFS.map((s) => (
            <button
              key={s.key}
              className={`btn btn-sm${status === s.key ? ' btn-primary' : ''}`}
              onClick={() => {
                setStatus(s.key);
                setPage(1);
              }}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <div className="card">
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>النطاق</th>
                <th>الشركة</th>
                <th>الباقة</th>
                <th>الحالة</th>
                <th>المستخدمون</th>
                <th>الاشتراك حتى</th>
                <th>القيمة</th>
              </tr>
            </thead>

            {list.isPending && <TableSkeleton cols={7} />}

            {list.data && (
              <tbody>
                {list.data.items.map((t) => {
                  const end = t.subscription?.currentPeriodEnd ?? t.trialEndsAt;
                  const soon =
                    end && new Date(end).getTime() - Date.now() < 7 * 86_400_000;
                  return (
                    <tr key={t.id}>
                      <td className="num">
                        <Link to={`/tenants/${t.id}`} className="link cell-strong">
                          {t.slug}
                        </Link>
                      </td>
                      <td>
                        {t.name}
                        {t.email && <div className="tiny mute num">{t.email}</div>}
                      </td>
                      <td className="cell-mute">{t.subscription?.plan.name ?? '—'}</td>
                      <td>
                        <Badge kind={TENANT_STATUS_MAP[t.status].kind}>
                          {TENANT_STATUS_MAP[t.status].label}
                        </Badge>
                      </td>
                      <td className="num cell-mute">{t.userCount}</td>
                      <td className={`num ${soon ? 'neg' : 'cell-mute'}`}>{date(end)}</td>
                      <td className="num">
                        {t.subscription ? money(t.subscription.price) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            )}
          </table>
        </div>

        {list.isError && <ErrorState error={list.error} onRetry={() => void list.refetch()} />}

        {list.data?.items.length === 0 && (
          <EmptyState
            title="لا مستأجرين"
            text="هيّئ أول شركة لتبدأ العمل على المنصة."
            action={
              <button className="btn btn-primary" onClick={() => setCreating(true)}>
                تهيئة شركة
              </button>
            }
          />
        )}

        {list.data && (
          <Pager
            page={list.data.page}
            pageCount={list.data.pageCount}
            total={list.data.total}
            pageSize={list.data.pageSize}
            onPage={setPage}
          />
        )}
      </div>

      {creating && (
        <ProvisionModal
          onClose={() => setCreating(false)}
          onDone={() => {
            setCreating(false);
            void queryClient.invalidateQueries({ queryKey: ['tenants'] });
            void queryClient.invalidateQueries({ queryKey: ['summary'] });
          }}
        />
      )}
    </div>
  );
}

/* ========================================================================== */

/** يقترح نطاقًا من اسم الشركة: أحرف لاتينية وأرقام وشرطات. */
function suggestSlug(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .slice(0, 40);
}

function ProvisionModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [form, setForm] = useState({
    name: '',
    slug: '',
    planCode: DEFAULT_PLANS[1]?.id ?? 'plan_pro',
    ownerName: '',
    ownerEmail: '',
    ownerPassword: '',
    phone: '',
    trialDays: String(LIFECYCLE.trialDays),
  });
  const [slugTouched, setSlugTouched] = useState(false);

  const set = (key: string, value: string) => setForm((f) => ({ ...f, [key]: value }));

  const plans = useQuery({
    queryKey: ['plans'],
    queryFn: () => api<Array<{ id: string; code: string; name: string; price: string }>>('/plans'),
  });

  const provision = useMutation({
    mutationFn: () =>
      api<{
        tenant: { id: string; slug: string };
        owner: { email: string; isNew: boolean; passwordIgnored: boolean };
      }>('/tenants', {
        method: 'POST',
        body: {
          slug: form.slug,
          name: form.name,
          planCode: form.planCode,
          ownerName: form.ownerName,
          ownerEmail: form.ownerEmail,
          ownerPassword: form.ownerPassword,
          phone: form.phone || null,
          trialDays: Number(form.trialDays) || 0,
        },
      }),
  });

  if (provision.isSuccess) {
    const { tenant, owner } = provision.data;
    return (
      <Modal
        title="تمت التهيئة"
        onClose={onDone}
        footer={
          <button className="btn btn-primary" onClick={onDone}>
            تم
          </button>
        }
      >
        <p className="mb-4">
          الشركة جاهزة على النطاق <span className="num strong">{tenant.slug}</span>.
        </p>
        {owner.isNew ? (
          <div className="alert alert-info">
            أُنشئ حساب المالك ببريد <span className="num">{owner.email}</span>. سلّمه بيانات
            الدخول عبر قناة آمنة، ولا تُرسل كلمة المرور في بريد عادي.
          </div>
        ) : (
          /*
           * التحذير صريح لا مجرد ملاحظة.
           *
           * كلمة المرور المكتوبة في النموذج لم تُستعمل، ومن يهيّئ الشركة
           * سيسلّمها للمالك ما لم يُنبَّه — فيصطدم المالك بشاشة دخول ترفض
           * ما تسلّمه ولا يعرف السبب.
           */
          <div className="alert alert-warning">
            <span className="strong">انتبه: كلمة المرور التي كتبتها لم تُستعمل.</span> البريد{' '}
            <span className="num">{owner.email}</span> له حساب على المنصة بالفعل، فأُضيف كمالك
            لهذه الشركة <span className="strong">بكلمة مروره الحالية</span>. الدخول يكون بها لا
            بما كتبته هنا.
          </div>
        )}

        <div className="row mt-4" style={{ gap: 8 }}>
          <a
            className="btn btn-primary"
            href={`${window.location.protocol}//${window.location.hostname}:5173/?tenant=${encodeURIComponent(tenant.slug)}`}
            target="_blank"
            rel="noreferrer"
          >
            فتح التطبيق
          </a>
          <Link to={`/tenants/${tenant.id}`} className="btn">
            ملف الشركة
          </Link>
        </div>
      </Modal>
    );
  }

  const valid =
    form.name.trim().length >= 2 &&
    form.slug.trim().length >= 3 &&
    form.ownerName.trim().length >= 2 &&
    /.+@.+\..+/.test(form.ownerEmail) &&
    form.ownerPassword.length >= 12;

  return (
    <Modal
      title="تهيئة شركة جديدة"
      onClose={onClose}
      wide
      footer={
        <>
          <button className="btn" onClick={onClose} type="button">
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            onClick={() => provision.mutate()}
            disabled={!valid || provision.isPending}
          >
            {provision.isPending ? 'جارٍ التهيئة' : 'تهيئة'}
          </button>
        </>
      }
    >
      {provision.isError && (
        <div className="alert alert-danger mb-4">
          {provision.error instanceof Error ? provision.error.message : 'تعذّرت التهيئة.'}
        </div>
      )}

      <p className="small mute mb-4">
        تُنشأ الشركة واشتراكها وحساب مالكها في عملية واحدة. المالك هو من يدير المستخدمين
        والصلاحيات داخل شركته بعد ذلك.
      </p>

      <div className="form-grid">
        <div className="field">
          <label className="label req" htmlFor="t-name">
            اسم الشركة
          </label>
          <input
            id="t-name"
            className="input"
            value={form.name}
            onChange={(e) => {
              set('name', e.target.value);
              // النطاق يتبع الاسم حتى يحرّره المستخدم بنفسه.
              if (!slugTouched) set('slug', suggestSlug(e.target.value));
            }}
            autoFocus
          />
        </div>

        <div className="field">
          <label className="label req" htmlFor="t-slug">
            النطاق الفرعي
          </label>
          <input
            id="t-slug"
            className="input num"
            dir="ltr"
            value={form.slug}
            onChange={(e) => {
              setSlugTouched(true);
              set('slug', e.target.value.toLowerCase());
            }}
          />
          <span className="tiny mute num" dir="ltr">
            {form.slug || 'company'}.goyone.com
          </span>
        </div>

        <div className="field">
          <label className="label" htmlFor="t-plan">
            الباقة
          </label>
          <select
            id="t-plan"
            className="select"
            value={form.planCode}
            onChange={(e) => set('planCode', e.target.value)}
          >
            {plans.data?.map((p) => (
              <option key={p.id} value={p.code}>
                {p.name} — {money(p.price)}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label className="label" htmlFor="t-trial">
            أيام التجربة
          </label>
          <input
            id="t-trial"
            className="input num"
            type="number"
            min="0"
            max="365"
            value={form.trialDays}
            onChange={(e) => set('trialDays', e.target.value)}
          />
          <span className="tiny mute">صفر يعني اشتراكًا نشطًا فورًا.</span>
        </div>

        <div className="field span-2">
          <label className="label" htmlFor="t-phone">
            هاتف الشركة
          </label>
          <input
            id="t-phone"
            className="input num"
            dir="ltr"
            value={form.phone}
            onChange={(e) => set('phone', e.target.value)}
          />
        </div>
      </div>

      <h4 className="section-title">حساب المالك</h4>

      <div className="form-grid">
        <div className="field">
          <label className="label req" htmlFor="t-owner">
            الاسم
          </label>
          <input
            id="t-owner"
            className="input"
            value={form.ownerName}
            onChange={(e) => set('ownerName', e.target.value)}
          />
        </div>

        <div className="field">
          <label className="label req" htmlFor="t-email">
            البريد الإلكتروني
          </label>
          <input
            id="t-email"
            className="input num"
            type="email"
            dir="ltr"
            value={form.ownerEmail}
            onChange={(e) => set('ownerEmail', e.target.value)}
          />
        </div>

        <div className="field span-2">
          <label className="label req" htmlFor="t-password">
            كلمة المرور الأولى
          </label>
          <input
            id="t-password"
            className="input"
            type="text"
            dir="ltr"
            value={form.ownerPassword}
            onChange={(e) => set('ownerPassword', e.target.value)}
          />
          <span className="tiny mute">
            اثنا عشر حرفًا على الأقل. سلّمها للمالك عبر قناة آمنة واطلب تغييرها بعد أول دخول.
          </span>
        </div>
      </div>
    </Modal>
  );
}
